import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { db } from '@/api/db';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '';

const PASSWORD_RULES = [
  { label: 'Mínimo 8 caracteres', test: v => v.length >= 8 },
  { label: '1 letra maiúscula', test: v => /[A-Z]/.test(v) },
  { label: '1 dígito', test: v => /[0-9]/.test(v) },
  { label: '1 caractere especial', test: v => /[^A-Za-z0-9]/.test(v) },
];

export default function SetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // loading | valid | invalid | success
  const [email, setEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      setErrorMsg('Link inválido. Nenhum token fornecido.');
      return;
    }

    fetch(`${API_URL}/api/auth/verify-token?token=${encodeURIComponent(token)}`)
      .then(async res => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Token inválido');
        }
        return res.json();
      })
      .then(data => {
        setEmail(data.email);
        setStatus('valid');
      })
      .catch(err => {
        setStatus('invalid');
        setErrorMsg(err.message || 'Token inválido ou expirado.');
      });
  }, [token]);

  const allRulesPass = PASSWORD_RULES.every(r => r.test(password));
  const passwordsMatch = password === confirm && confirm.length > 0;
  const canSubmit = allRulesPass && passwordsMatch && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Erro ao definir senha');
      }

      const data = await res.json();

      // Auto-login: save token and user
      localStorage.setItem('portus_token', data.token);
      localStorage.setItem('portus_user', JSON.stringify(data.user));

      setStatus('success');

      // Redirect after brief delay
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } catch (err) {
      setSubmitError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #ffffff 0%, #edfbf4 55%, #f5fef9 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <style>{`
        @keyframes drift1 {
          0%,100% { transform: translate(0px,    0px)   scale(1);    }
          30%      { transform: translate(160px, -80px)  scale(1.15); }
          65%      { transform: translate(-60px,  120px) scale(0.90); }
        }
        @keyframes drift2 {
          0%,100% { transform: translate(0px,    0px)   scale(1);    }
          35%      { transform: translate(-130px,  60px) scale(1.20); }
          70%      { transform: translate(100px, -100px) scale(0.88); }
        }
        .sp-input {
          width: 100%;
          padding: 12px 14px;
          border: 1.5px solid #E5E9EA;
          border-radius: 12px;
          font-size: 14px;
          color: #2E3233;
          outline: none;
          background: #fff;
          box-sizing: border-box;
          transition: border-color 0.15s, box-shadow 0.15s;
          font-family: inherit;
        }
        .sp-input:focus {
          border-color: #00DCA4;
          box-shadow: 0 0 0 3px rgba(0,220,164,0.12);
        }
        .sp-input:disabled {
          background: #F2F4F4;
          color: #99A6AB;
        }
      `}</style>

      {/* Spotlights */}
      <div style={{
        position: 'absolute', borderRadius: '50%', pointerEvents: 'none',
        width: 700, height: 700, top: '-20%', left: '-15%',
        background: 'radial-gradient(circle, rgba(0,220,164,0.22) 0%, transparent 65%)',
        filter: 'blur(80px)',
        animation: 'drift1 16s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', borderRadius: '50%', pointerEvents: 'none',
        width: 580, height: 580, bottom: '-18%', right: '-8%',
        background: 'radial-gradient(circle, rgba(0,220,164,0.18) 0%, transparent 65%)',
        filter: 'blur(70px)',
        animation: 'drift2 20s ease-in-out infinite',
      }} />

      {/* Card */}
      <div style={{
        position: 'relative', zIndex: 10,
        width: '100%', maxWidth: 420,
        margin: '0 16px',
        background: '#fff',
        borderRadius: 24,
        padding: '44px 40px',
        boxShadow: '0 8px 48px 0 rgba(0,102,76,0.10), 0 1px 0 0 rgba(0,220,164,0.15)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <img src="/logo.png" alt="Portüs Talent Pro" style={{ height: 52, width: 'auto' }} />
        </div>

        {/* Loading */}
        {status === 'loading' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: '#00DCA4' }} />
            <p style={{ fontSize: 14, color: '#646C6F', marginTop: 12 }}>Verificando link...</p>
          </div>
        )}

        {/* Invalid token */}
        {status === 'invalid' && (
          <div style={{ textAlign: 'center' }}>
            <XCircle className="w-12 h-12 mx-auto" style={{ color: '#C0392B' }} />
            <h1 style={{ fontSize: 20, fontWeight: 800, color: '#003326', margin: '16px 0 8px', fontFamily: 'Poppins, sans-serif' }}>
              Link inválido
            </h1>
            <p style={{ fontSize: 14, color: '#646C6F', marginBottom: 24, lineHeight: 1.5 }}>
              {errorMsg}
            </p>
            <a href="/login" style={{
              display: 'inline-block', padding: '12px 28px',
              background: '#00DCA4', color: '#003326',
              borderRadius: 99, fontSize: 14, fontWeight: 700,
              textDecoration: 'none',
            }}>
              Ir para o login
            </a>
          </div>
        )}

        {/* Success */}
        {status === 'success' && (
          <div style={{ textAlign: 'center' }}>
            <CheckCircle className="w-12 h-12 mx-auto" style={{ color: '#00DCA4' }} />
            <h1 style={{ fontSize: 20, fontWeight: 800, color: '#003326', margin: '16px 0 8px', fontFamily: 'Poppins, sans-serif' }}>
              Senha definida!
            </h1>
            <p style={{ fontSize: 14, color: '#646C6F' }}>
              Redirecionando para a plataforma...
            </p>
          </div>
        )}

        {/* Valid — show form */}
        {status === 'valid' && (
          <>
            <h1 style={{
              fontSize: 22, fontWeight: 800, color: '#003326',
              marginBottom: 4, fontFamily: 'Poppins, sans-serif',
            }}>
              Defina sua senha
            </h1>
            <p style={{ fontSize: 14, color: '#99A6AB', marginBottom: 28 }}>
              Crie uma senha para acessar o Portüs Talent Pro.
            </p>

            <form onSubmit={handleSubmit}>
              {/* Email (read-only) */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#646C6F', marginBottom: 6 }}>
                  E-mail
                </label>
                <input
                  className="sp-input"
                  type="email"
                  value={email}
                  disabled
                />
              </div>

              {/* Password */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#646C6F', marginBottom: 6 }}>
                  Senha
                </label>
                <input
                  className="sp-input"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
                {/* Rules */}
                {password.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                    {PASSWORD_RULES.map(rule => {
                      const pass = rule.test(password);
                      return (
                        <span key={rule.label} style={{
                          fontSize: 11, color: pass ? '#00664C' : '#99A6AB',
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                          {pass ? <CheckCircle className="w-3 h-3" /> : <span style={{
                            width: 12, height: 12, borderRadius: '50%',
                            border: '1.5px solid #C8CED0', display: 'inline-block', flexShrink: 0,
                          }} />}
                          {rule.label}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Confirm password */}
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#646C6F', marginBottom: 6 }}>
                  Confirmar senha
                </label>
                <input
                  className="sp-input"
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  required
                />
                {confirm.length > 0 && !passwordsMatch && (
                  <p style={{ fontSize: 11, color: '#C0392B', marginTop: 6 }}>
                    As senhas não coincidem.
                  </p>
                )}
              </div>

              {submitError && (
                <div style={{
                  marginBottom: 16, padding: '10px 14px',
                  background: '#FFF1F1', borderRadius: 10,
                  fontSize: 13, color: '#C0392B',
                }}>
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  width: '100%', padding: '14px',
                  background: canSubmit ? '#00DCA4' : '#E5E9EA',
                  color: canSubmit ? '#003326' : '#99A6AB',
                  borderRadius: 99, fontSize: 15, fontWeight: 700,
                  border: 'none', cursor: canSubmit ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'background 0.15s, color 0.15s',
                }}
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? 'Salvando...' : 'Definir senha e entrar'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
